/**
 * Monitoring erreurs (web) — Phase 11, incrément 11.2 (hardening prod).
 *
 * Remonte les erreurs JS du front à Sentry. Deux garde-fous :
 *
 *   - **No-op sans DSN** : le DSN est lu depuis `VITE_SENTRY_DSN`
 *     (variable de build Vite, injectée en CI/prod). Absent → aucune
 *     init, aucun réseau. Le dev local et les tests ne dépendent donc
 *     d'aucune config Sentry.
 *   - **Lazy-load** : `@sentry/browser` (~20-30 KB gzip) est importé
 *     **dynamiquement** seulement quand un DSN est présent. La bundle
 *     principale reste inchangée (le SDK part dans un chunk séparé,
 *     jamais téléchargé en l'absence de DSN). Choix acté Phase 11.2
 *     pour préserver le poids de la PWA suivi au KB près.
 *
 * Usage : `void initMonitoring();` au boot (`main.ts`), fire-and-forget.
 */

/**
 * Lit le DSN Sentry depuis l'env de build Vite. Retourne `undefined`
 * si absent ou vide. Isolé pour être testable sans déclencher l'import
 * dynamique.
 */
export function getSentryDsn(): string | undefined {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  return typeof dsn === 'string' && dsn.length > 0 ? dsn : undefined;
}

/**
 * Initialise le monitoring si un DSN est configuré. Retourne `true`
 * si Sentry a été chargé + initialisé, `false` si désactivé (pas de
 * DSN) ou en cas d'échec de chargement (best-effort : le monitoring
 * ne doit jamais casser l'app).
 */
export async function initMonitoring(): Promise<boolean> {
  const dsn = getSentryDsn();
  if (!dsn) return false;
  try {
    const Sentry = await import('@sentry/browser');
    Sentry.init({
      dsn,
      environment: import.meta.env.MODE,
      // Capture d'erreurs uniquement au lancement — pas de tracing
      // perf (coûteux en quota). À activer plus tard si besoin.
      tracesSampleRate: 0,
    });
    return true;
  } catch {
    // Échec de chargement du chunk (réseau, CDN) : on n'a pas de
    // monitoring, mais l'app continue normalement.
    return false;
  }
}
